import { Command } from 'cmdk';
import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import sanitizeHtml from 'sanitize-html';
import type { IconName } from '@customerio/pluma-icons';
import {
  TextField,
  Box,
  ModalPrimitiveDialog,
  ModalPrimitiveOverlay,
  ModalPrimitive,
  EmptyStateDescription,
  EmptyState,
  EmptyStateTitle,
  Icon,
  Text,
  Flex,
  FormattedDateTime,
  EmptyStateGraphic,
  Spinner,
  KeyboardShortcut,
  InlineStack,
  type ModalPrimitiveProps,
  List,
  ListItem,
  type PlumaManagedModalProps,
  SegmentedControl,
} from '@customerio/pluma-components/react';
import { useIsAgentFullscreen } from '@/features/copilot';
import type { useCopilotSessionContext, useCopilotSidebar } from '@/features/copilot';
import { useServicesQuery, useServicesInfiniteQuery } from '@/services/api';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import type { useCurrentWorkspace } from '@/context/current-workspace';
import * as styles from './index.css';
import type { SearchResult } from '@/services/auth/types';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { LABEL, SEARCH_RESULT_HEADINGS, UNIVERSAL_SEARCH_PREFIXES, scopedUniversalSearchQuery } from './constants';
import type { UniversalSearchPrefix } from './constants';
import {
  computeItemPosition,
  getIconNameFromSearchResult,
  buildPeopleSearchQuery,
  groupSearchResults,
  mapCustomerToSearchResult,
} from './utils';
import { useStoredHydraCommands } from './use-stored-hydra-commands';
import { useLocalSearchResults } from './use-local-search-results';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import {
  PlumaModal as PlumaModalCss,
  PlumaBreadcrumbs as PlumaBreadcrumbsCss,
} from '@customerio/pluma-components/css/components';
import type { useAnalytics } from '@/services/analytics';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';
import { useKeyHold } from '@tanstack/react-hotkeys';
import type { CustomerResult } from '@/services/auth/types';
import { QuickLookPanel } from './quick-look';
import { useQuickLook } from './quick-look/use-quick-look';
import clsx from 'clsx';

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: UNIVERSAL_SEARCH_PREFIXES.segments, label: 'Segments' },
  { value: UNIVERSAL_SEARCH_PREFIXES.campaigns, label: 'Campaigns' },
  { value: UNIVERSAL_SEARCH_PREFIXES.actions, label: 'Actions' },
  { value: UNIVERSAL_SEARCH_PREFIXES.templates, label: 'Templates' },
  { value: UNIVERSAL_SEARCH_PREFIXES.navigation, label: 'Navigation' },
  { value: 'people', label: 'People' },
] as const;

type ActiveFilter = (typeof FILTER_OPTIONS)[number]['value'];

const sharedQueryOptions = {
  staleTime: 1000 * 30, // Consider search results stale after 30 seconds
  retryOnMount: false,
  forwardAbortSignal: true,
} as const;

type CommandDialogData = {
  session: ReturnType<typeof useCopilotSessionContext>;
  workspace: ReturnType<typeof useCurrentWorkspace>;
  initialInputValue?: string;
  initialActiveFilter?: string;
  persistInputValue: (value: string) => void;
  persistActiveFilter: (value: string) => void;
  track: ReturnType<typeof useAnalytics>['track'];
  openCopilot: ReturnType<typeof useCopilotSidebar>['openCopilot'];
};

export function CommandDialog({ data, onClose, modalState }: PlumaManagedModalProps<CommandDialogData>) {
  const { isOpen: isDialogOpen, onClose: onModalManagerClose, onCloseComplete, modalContext } = modalState;
  const isUniversalSearchEnabled = useFeatureFlag(AccountFeatureFlag.UniversalSearchDialog);
  const isUniversalSearchMultiEnabled = useFeatureFlag(AccountFeatureFlag.UniversalSearchMulti);
  const isAiAssistantV2 = useFeatureFlag(AccountFeatureFlag.AiAssistantV2);
  const isAiEnabled = useFeatureFlag(AccountFeatureFlag.AiEnabled);
  const isAgentFullscreen = useIsAgentFullscreen();
  const showAskAssistant = isAiAssistantV2 && isAiEnabled && !isAgentFullscreen;
  const safeNavigate = useSafeNavigate();
  const [inputValue, setInputValue] = useState(data.initialInputValue ?? '');
  const [debouncedInputValue] = useDebouncedValue(inputValue.trim(), 250);

  const isQuickLookEnabled = useFeatureFlag(AccountFeatureFlag.UniversalSearchQuickLook);
  const [selectedValue, setSelectedValue] = useState('');
  const [selectedResult, setSelectedResult] = useState<SearchResult | null>(null);

  const isMetaHeld = useKeyHold('Meta');
  const isCtrlHeld = useKeyHold('Control');

  const { session, workspace, persistInputValue, persistActiveFilter, track, openCopilot } = data;
  const workspaceId = workspace?.id ?? 0;

  const handleInputFocus = useCallback((event: Event & { target: HTMLInputElement }) => {
    event.target.select();
  }, []);

  const queryStartTimeRef = useRef<number | null>(null);
  const previousDebouncedInputRef = useRef(debouncedInputValue);

  const openTimeRef = useRef(Date.now());
  const previousIsDialogOpenRef = useRef(isDialogOpen);
  const listRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // used for tracking the time the user started typing to get the time to first result
  if (debouncedInputValue !== previousDebouncedInputRef.current) {
    previousDebouncedInputRef.current = debouncedInputValue;
    if (debouncedInputValue.length > 0) {
      queryStartTimeRef.current = Date.now();
    }
  }
  // used for tracking the time the dialog was opened to get the time open
  if (isDialogOpen !== previousIsDialogOpenRef.current) {
    previousIsDialogOpenRef.current = isDialogOpen;
    if (isDialogOpen === true) {
      openTimeRef.current = Date.now();
    }
  }

  const isSearchingEnabled =
    isUniversalSearchEnabled && isDialogOpen === true && debouncedInputValue.length > 0 && workspaceId > 0;

  const [activeFilter, setActiveFilter] = useState<ActiveFilter>(() => {
    if (!isUniversalSearchMultiEnabled) return 'all';
    const stored = data.initialActiveFilter;
    const validFilters: string[] = ['all', 'people', ...Object.values(UNIVERSAL_SEARCH_PREFIXES)];
    return validFilters.includes(stored ?? '') ? (stored as ActiveFilter) : 'all';
  });

  const handleFilterChange = useCallback(
    (value: ActiveFilter) => {
      setActiveFilter(value);
      persistActiveFilter(value);
    },
    [persistActiveFilter],
  );

  const isPeopleFilterActive = activeFilter === 'people';
  const activeSearchPrefix = (Object.values(UNIVERSAL_SEARCH_PREFIXES) as string[]).includes(activeFilter)
    ? (activeFilter as UniversalSearchPrefix)
    : null;

  const searchQuery = useMemo(
    () =>
      activeSearchPrefix != null
        ? scopedUniversalSearchQuery(activeSearchPrefix, debouncedInputValue)
        : debouncedInputValue,
    [activeSearchPrefix, debouncedInputValue],
  );

  const peopleSearchQuery = useMemo(
    () => (isPeopleFilterActive ? buildPeopleSearchQuery(debouncedInputValue) : null),
    [isPeopleFilterActive, debouncedInputValue],
  );

  const {
    data: searchData,
    isLoading: isSearchLoading,
    isError: isSearchError,
    dataUpdatedAt: searchDataUpdatedAt,
  } = useServicesQuery(
    'search',
    { params: { workspaceId }, query: { q: searchQuery } },
    {
      enabled: isSearchingEnabled && !isPeopleFilterActive,
      ...sharedQueryOptions,
    },
  );

  const {
    data: peopleInfiniteData,
    isLoading: isPeopleLoading,
    isError: isPeopleError,
    dataUpdatedAt: peopleDataUpdatedAt,
    fetchNextPage: fetchNextPeoplePage,
    hasNextPage: hasNextPeoplePage,
    isFetchingNextPage: isFetchingNextPeoplePage,
  } = useServicesInfiniteQuery('getPeople', {
    queryKey: [workspaceId, peopleSearchQuery],
    initialPageParam: 1,
    queryData: ({ pageParam }) => ({
      params: { workspaceId },
      query: { email: peopleSearchQuery ?? undefined, asc: false, page: pageParam },
    }),
    getNextPageParam: (lastPage) => {
      if (lastPage.meta == null) return undefined;
      const totalPages = Math.ceil(lastPage.meta.pagination.total / lastPage.meta.pagination.size);
      return lastPage.meta.pagination.page < totalPages ? lastPage.meta.pagination.page + 1 : undefined;
    },
    enabled: isSearchingEnabled && isPeopleFilterActive && peopleSearchQuery != null,
    ...sharedQueryOptions,
  });

  const isError = isPeopleFilterActive ? isPeopleError : isSearchError;
  const dataUpdatedAt = isPeopleFilterActive ? peopleDataUpdatedAt : searchDataUpdatedAt;

  const peopleData = useMemo(() => {
    if (peopleInfiniteData == null) return undefined;
    return peopleInfiniteData.pages.flatMap((page) =>
      page.customers.map((customer) => mapCustomerToSearchResult(customer, workspaceId)),
    ) satisfies CustomerResult[];
  }, [peopleInfiniteData, workspaceId]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [activeFilter]);

  useEffect(() => {
    if (!isPeopleFilterActive) return;
    const sentinel = sentinelRef.current;
    const list = listRef.current;
    if (sentinel == null || list == null) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting === true && hasNextPeoplePage === true && isFetchingNextPeoplePage === false) {
          void fetchNextPeoplePage();
        }
      },
      { root: list, threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isPeopleFilterActive, hasNextPeoplePage, isFetchingNextPeoplePage, fetchNextPeoplePage]);

  const closeDialog = useCallback(() => {
    onClose();
  }, [onClose]);

  const allApiResults = useMemo(() => {
    if (isPeopleFilterActive) return peopleData ?? [];
    return searchData?.results ?? [];
  }, [isPeopleFilterActive, searchData?.results, peopleData]);

  const isLoading = isPeopleFilterActive ? isPeopleLoading : isSearchLoading;

  const groupedResults = useMemo(() => groupSearchResults(allApiResults), [allApiResults]);

  const resultsByKey = useMemo(() => {
    const map = new Map<string, SearchResult>();
    for (const [, items] of groupedResults) {
      for (let index = 0; index < items.length; index++) {
        const item = items[index];
        const key = `${item.kind}-${index}-${item.doc_id ?? encodeURIComponent(item.url)}`.toLowerCase();
        map.set(key, item);
      }
    }
    return map;
  }, [groupedResults]);

  const handleValueChange = useCallback(
    (value: string) => {
      setSelectedValue(value);
      setSelectedResult(resultsByKey.get(value) ?? null);
    },
    [resultsByKey],
  );

  const handleQuickLookOpen = useCallback(
    (result: SearchResult) => {
      void track('universalSearch', 'quickLookOpened', {
        'Result Kind': result.kind,
        Query: debouncedInputValue,
      });
    },
    [track, debouncedInputValue],
  );

  const quickLook = useQuickLook({
    enabled: isQuickLookEnabled && !isLoading,
    selectedResult,
    onOpen: handleQuickLookOpen,
    target: dialogRef.current,
  });

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      persistInputValue(value);
      quickLook.close();
    },
    [persistInputValue, quickLook.close],
  );

  // Using inputValue instead of debouncedInputValue as we aren't calling
  // an expensive API like /search and there probably won't be many commands to filter by
  const hydraCommands = useStoredHydraCommands(inputValue);

  // Local search results — handlers are called on-demand with the debounced query
  const localSearchGroups = useLocalSearchResults(debouncedInputValue);

  const apiResultsCount = allApiResults.length;
  const localSearchCount = localSearchGroups.reduce((sum, g) => sum + g.results.length, 0);

  const hasNoApiResults = allApiResults.length === 0;
  // TODO: Local commands ("Quick actions") and local search results are shown
  // regardless of the active filter. This is likely confusing — e.g. showing "Quick actions"
  // while the user has filtered to "Segments" or "People". The pattern for suppressing
  // them under specific filters is TBD.
  const hasLocalCommands = hydraCommands.length > 0;
  const hasLocalSearchResults = localSearchGroups.length > 0;
  const hasNoResults = hasNoApiResults && !hasLocalCommands && !hasLocalSearchResults;
  const askAssistantVisible =
    showAskAssistant &&
    debouncedInputValue.length > 0 &&
    !isLoading &&
    !hasNoResults &&
    !isError &&
    !isPeopleFilterActive;

  const totalVisibleCount = useMemo(() => {
    return (askAssistantVisible ? 1 : 0) + hydraCommands.length + localSearchCount + apiResultsCount;
  }, [askAssistantVisible, hydraCommands.length, localSearchCount, apiResultsCount]);

  const timeToFirstResultMs =
    queryStartTimeRef.current != null ? Math.max(0, dataUpdatedAt - queryStartTimeRef.current) : 0;

  const handleItemSelect = useCallback(
    (url: string, kind: SearchResult['kind'], position: number) => {
      const openInNewTab = isMetaHeld || isCtrlHeld;

      void track('universalSearch', 'itemSelected', {
        'Item Type': 'search_result',
        'Result Kind': kind,
        'Open in New Tab': openInNewTab,
        Query: debouncedInputValue,
        'Result Position': position,
        'Results Count': totalVisibleCount,
        'API Results Count': apiResultsCount,
        'Time to First Result Ms': timeToFirstResultMs,
        // TODO: Add 'Active Filter': activeFilter once we settle on a filtering
        // pattern. Since quick-look is internally feature-flagged, this is low-risk to add later
        // and will help measure filter adoption and conversion.
      });
      if (openInNewTab) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        safeNavigate(url);
        closeDialog();
      }
    },
    [
      safeNavigate,
      closeDialog,
      track,
      isMetaHeld,
      isCtrlHeld,
      debouncedInputValue,
      dataUpdatedAt,
      totalVisibleCount,
      apiResultsCount,
      timeToFirstResultMs,
    ],
  );

  const handleLocalCommandSelect = useCallback(
    (onSelect: () => void | Promise<void>, position: number) => {
      void track('universalSearch', 'itemSelected', {
        'Item Type': 'local_command',
        'Result Kind': 'command',
        Query: debouncedInputValue,
        'Result Position': position,
        'Results Count': totalVisibleCount,
        'API Results Count': apiResultsCount,
        'Open in New Tab': false,
        'Time to First Result Ms': timeToFirstResultMs,
      });
      void Promise.resolve(onSelect());
      closeDialog();
    },
    [closeDialog, track, debouncedInputValue, totalVisibleCount, apiResultsCount, timeToFirstResultMs],
  );

  const handleLocalSearchSelect = useCallback(
    (onSelect: () => void | Promise<void>, position: number) => {
      void track('universalSearch', 'itemSelected', {
        'Item Type': 'local_search',
        'Result Kind': 'search',
        Query: debouncedInputValue,
        'Result Position': position,
        'Results Count': totalVisibleCount,
        'API Results Count': apiResultsCount,
        'Open in New Tab': false,
        'Time to First Result Ms': timeToFirstResultMs,
      });
      void Promise.resolve(onSelect());
      closeDialog();
    },
    [closeDialog, track, debouncedInputValue, totalVisibleCount, apiResultsCount, timeToFirstResultMs],
  );

  const handleAskAssistant = useCallback(
    (resultPosition: number) => {
      void track('universalSearch', 'itemSelected', {
        'Item Type': 'ask_assistant',
        'Result Kind': 'assistant',
        Query: debouncedInputValue,
        'Result Position': resultPosition,
        'Results Count': totalVisibleCount,
        'API Results Count': apiResultsCount,
        'Open in New Tab': false,
        'Time to First Result Ms': timeToFirstResultMs,
      });
      openCopilot({ triggerSource: 'command_dialog' });
      if (debouncedInputValue.length > 0) {
        void session.sendMessage(debouncedInputValue).catch(sentryCaptureException);
      }
      closeDialog();
    },
    [
      closeDialog,
      track,
      debouncedInputValue,
      session,
      openCopilot,
      totalVisibleCount,
      apiResultsCount,
      timeToFirstResultMs,
    ],
  );

  const handleDialogClose = useCallback<NonNullable<ModalPrimitiveProps['onClose']>>(
    (_event, reason) => {
      void track('universalSearch', 'closed', {
        Reason: reason ?? 'dismiss',
        'Had Query': !isEmptyString(inputValue.trim()),
        Query: inputValue.trim(),
        'Time Open Ms': Date.now() - openTimeRef.current,
        // TODO: Add 'Active Filter': activeFilter once we settle on a filtering
        // pattern. Since quick-look is internally feature-flagged, this is low-risk to add later
        // and will help measure filter adoption and conversion.
      });
      onModalManagerClose?.(_event, reason);
    },
    [track, onModalManagerClose, inputValue],
  );

  return (
    <ModalPrimitive
      initialFocusIndex={-1}
      animationTransitionDuration={125}
      isOpen={isDialogOpen ?? false}
      onClose={handleDialogClose}
      onCloseComplete={onCloseComplete}
      modalContext={modalContext}
    >
      <ModalPrimitiveOverlay className={PlumaModalCss.classNames.modalOverlay}>
        <ModalPrimitiveDialog ref={dialogRef} className={styles.dialog}>
          <Command loop label={LABEL} shouldFilter={false} value={selectedValue} onValueChange={handleValueChange}>
            <Command.Input asChild value={inputValue} onValueChange={handleInputChange}>
              <TextField
                autoFocus={true}
                placeholder={LABEL}
                onInput={(event) => handleInputChange(event.target.value)}
                onKeyUp={quickLook.handleCursorChange}
                onClick={quickLook.handleCursorChange}
                onFocus={handleInputFocus}
                ariaLabel="Use the command palette to search for campaigns, templates, segments, or anything else in this workspace."
                className={`${styles.largeTextField} fs-mask`}
                icon="search"
                iconColor="accent"
                size="sm"
                ml="200"
                mr={hasNoResults ? '200' : undefined}
                mt="100"
              />
            </Command.Input>
            {isUniversalSearchMultiEnabled ? (
              <InlineStack alignBlock="center" alignInline="center" mx="200" py="100">
                <SegmentedControl
                  ariaLabel="Filter by"
                  size="sm"
                  name="command_dialog_filter"
                  value={activeFilter}
                  onChange={(value) => {
                    handleFilterChange(value as ActiveFilter);
                    quickLook.close();
                  }}
                  options={[...FILTER_OPTIONS]}
                  w="full"
                />
              </InlineStack>
            ) : null}
            <Flex direction="row" overflow="hidden">
              <Box flex={1} minW="0" overflow="hidden">
                <Command.List ref={listRef}>
                  {hasLocalCommands ? (
                    <Command.Group heading="Quick actions">
                      {hydraCommands.map((cmd, cmdIndex) => (
                        <Command.Item
                          key={`local-${cmd.id}`}
                          value={`local-${cmd.id}`}
                          onSelect={() =>
                            handleLocalCommandSelect(
                              cmd.onSelect,
                              computeItemPosition({
                                hydraCommandsCount: hydraCommands.length,
                                localSearchCount,
                                section: 'quickActions',
                                groupOffset: 0,
                                itemIndex: cmdIndex,
                              }),
                            )
                          }
                          asChild
                        >
                          <Box alignItems="flex-start" gap="050" display="flex">
                            <Icon
                              mt="025"
                              size="sm"
                              sizeVersion="v2"
                              color="base"
                              name={(cmd.icon ?? 'command') as IconName}
                            />
                            <Flex direction="column" w="full">
                              <Text text="product-p" truncate>
                                {cmd.label}
                              </Text>
                              {cmd.description != null ? (
                                <Text size="xxs" color="subtle" truncate>
                                  {cmd.description}
                                </Text>
                              ) : null}
                            </Flex>
                          </Box>
                        </Command.Item>
                      ))}
                    </Command.Group>
                  ) : null}
                  {hasLocalSearchResults ? (
                    <>
                      {localSearchGroups.map((group, groupIndex) => {
                        const groupOffset = localSearchGroups
                          .slice(0, groupIndex)
                          .reduce((sum, g) => sum + g.results.length, 0);
                        return (
                          <Command.Group key={`search-${group.heading}`} heading={group.heading}>
                            {group.results.map((result, resultIndex) => (
                              <Command.Item
                                key={`search-${result.id}`}
                                value={`search-${result.id}`}
                                onSelect={() =>
                                  handleLocalSearchSelect(
                                    result.onSelect,
                                    computeItemPosition({
                                      hydraCommandsCount: hydraCommands.length,
                                      localSearchCount,
                                      section: 'localSearch',
                                      groupOffset,
                                      itemIndex: resultIndex,
                                    }),
                                  )
                                }
                                asChild
                              >
                                <Box alignItems="flex-start" gap="050" display="flex">
                                  <Icon
                                    mt="025"
                                    size="sm"
                                    sizeVersion="v2"
                                    color="base"
                                    name={(result.icon ?? 'search') as IconName}
                                  />
                                  <Flex direction="column" w="full">
                                    <Text text="product-p" truncate>
                                      {result.label}
                                    </Text>
                                    {result.description != null ? (
                                      <Text size="xxs" color="subtle" truncate>
                                        {result.description}
                                      </Text>
                                    ) : null}
                                  </Flex>
                                </Box>
                              </Command.Item>
                            ))}
                          </Command.Group>
                        );
                      })}
                    </>
                  ) : null}
                  {(hasLocalCommands || hasLocalSearchResults) && isLoading ? (
                    <Command.Loading asChild>
                      <EmptyState>
                        <EmptyStateGraphic>
                          <Spinner className={styles.spinner} color="information" />
                        </EmptyStateGraphic>
                        <EmptyStateTitle>Searching in {workspace?.name ?? 'this workspace'}…</EmptyStateTitle>
                      </EmptyState>
                    </Command.Loading>
                  ) : null}
                  {!hasLocalCommands && !hasLocalSearchResults && isLoading ? (
                    <Command.Loading asChild>
                      <EmptyState>
                        <EmptyStateGraphic>
                          <Spinner className={styles.spinner} color="information" />
                        </EmptyStateGraphic>
                        <EmptyStateTitle>
                          Searching for "<span className="fs-mask">{debouncedInputValue}</span>"
                        </EmptyStateTitle>
                        <EmptyStateDescription>
                          Hang on while we search in {workspace?.name ?? 'this workspace'}
                        </EmptyStateDescription>
                      </EmptyState>
                    </Command.Loading>
                  ) : null}
                  {debouncedInputValue.length === 0 && !hasLocalCommands && !hasLocalSearchResults ? (
                    <Command.Empty asChild>
                      <EmptyState>
                        <EmptyStateTitle icon="search">Search {workspace?.name ?? 'Workspace'}</EmptyStateTitle>
                        <EmptyStateDescription>
                          Try searching for campaigns, templates, segments, or anything else in this workspace.
                        </EmptyStateDescription>
                      </EmptyState>
                    </Command.Empty>
                  ) : null}
                  {debouncedInputValue.length > 0 && !isLoading && (hasNoResults || isError) ? (
                    <Command.Empty asChild>
                      <EmptyState>
                        <EmptyStateTitle icon="search">
                          No results found for <span className="fs-mask">{debouncedInputValue}</span>
                        </EmptyStateTitle>
                        <EmptyStateDescription>Try adjusting your search to find matches.</EmptyStateDescription>
                      </EmptyState>
                    </Command.Empty>
                  ) : null}
                  {allApiResults.length > 0 ? (
                    <>
                      {groupedResults.map(([kind, items], groupIndex) => {
                        const groupOffset = groupedResults
                          .slice(0, groupIndex)
                          .reduce((sum, [, groupItems]) => sum + groupItems.length, 0);
                        return (
                          <Command.Group
                            key={kind}
                            heading={SEARCH_RESULT_HEADINGS?.[kind] ?? `Search Results for '${kind}'`}
                          >
                            {items.map((item, index) => {
                              const url = new URL(item.url);
                              const safeUrlValue = url.pathname + url.search;
                              const hasDescription = item.kind === 'campaign' || item.kind === 'segment';
                              const snippet = item.kind === 'template' ? item.snippets?.[0] : undefined;
                              const showBreadcrumbs =
                                item.kind === 'action' && isEmptyString(item.other.campaign_name) === false;
                              const key = `${item.kind}-${index}-${item.doc_id ?? encodeURIComponent(item.url)}`;

                              const isArchived =
                                (item.kind === 'campaign' || item.kind === 'segment') &&
                                item.other?.archived === 'true';

                              return (
                                <Command.Item
                                  onSelect={() =>
                                    handleItemSelect(
                                      safeUrlValue,
                                      item.kind,
                                      computeItemPosition({
                                        hydraCommandsCount: hydraCommands.length,
                                        localSearchCount,
                                        section: 'apiResults',
                                        groupOffset,
                                        itemIndex: index,
                                      }),
                                    )
                                  }
                                  key={key}
                                  value={key}
                                  asChild
                                >
                                  <Box alignItems="flex-start" gap="050" display="flex">
                                    <Icon
                                      mt="025"
                                      name={getIconNameFromSearchResult(item)}
                                      size="sm"
                                      sizeVersion="v2"
                                      color="base"
                                    />
                                    <Flex direction="column" gap="025" w="full">
                                      <Flex direction="column" w="full">
                                        {isArchived ? (
                                          <InlineStack alignBlock="center" gap="100">
                                            <Text text="product-p" className="fs-mask" truncate flex={1} minW="0">
                                              {item.title}
                                            </Text>
                                            <Icon name="archive" size="xs" sizeVersion="v2" color="subtle" />
                                          </InlineStack>
                                        ) : (
                                          <Text text="product-p" className="fs-mask" truncate w="full">
                                            {item.title}
                                          </Text>
                                        )}
                                        {hasDescription && item.other?.description != null ? (
                                          <Text size="xxs" color="subtle" className="fs-mask" truncate w="full">
                                            {item.other.description}
                                          </Text>
                                        ) : null}
                                        {/* Snippets from Discovery Engine contain <em> tags for search term highlighting */}
                                        {snippet != null ? (
                                          <Text size="xxs" color="subtle" className="fs-mask" truncate w="full">
                                            <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(snippet) }} />
                                          </Text>
                                        ) : null}
                                      </Flex>
                                      {showBreadcrumbs ? (
                                        <List
                                          styleType="none"
                                          className={PlumaBreadcrumbsCss.classNames.breadcrumbsList}
                                          w="full"
                                        >
                                          <ListItem className={PlumaBreadcrumbsCss.classNames.breadcrumbsItem} truncate>
                                            Campaigns
                                          </ListItem>
                                          <ListItem className={PlumaBreadcrumbsCss.classNames.breadcrumbsItem} truncate>
                                            {item.other.campaign_name}
                                          </ListItem>
                                        </List>
                                      ) : null}
                                      {item.other?.created_at != null && item.other.created_at != null ? (
                                        <Text size="xxs" color="subtle" truncate w="full">
                                          Created at{' '}
                                          <FormattedDateTime
                                            className="fs-mask"
                                            date={item.other.created_at}
                                            format="medium"
                                          />
                                        </Text>
                                      ) : null}
                                    </Flex>
                                  </Box>
                                </Command.Item>
                              );
                            })}
                          </Command.Group>
                        );
                      })}
                      {isPeopleFilterActive ? <div ref={sentinelRef} style={{ height: '1px' }} /> : null}
                    </>
                  ) : null}
                  {isPeopleFilterActive ? (
                    <>
                      {isFetchingNextPeoplePage === true ? (
                        <Command.Loading asChild>
                          <Box display="flex" justifyContent="center" p="100">
                            <Spinner className={styles.spinner} color="information" />
                          </Box>
                        </Command.Loading>
                      ) : null}
                    </>
                  ) : null}
                  {askAssistantVisible ? (
                    <Command.Item
                      key="ask-assistant"
                      value="ask-assistant"
                      onSelect={() => handleAskAssistant(hydraCommands.length + localSearchCount + apiResultsCount + 1)}
                      asChild
                    >
                      <Box alignItems="flex-start" gap="050" display="flex">
                        <Icon mt="025" size="sm" sizeVersion="v2" color="base" name="sparkle" />
                        <Flex direction="column" w="full">
                          <Text text="product-p" truncate w="full">
                            Ask Agent
                          </Text>
                          <Text size="xxs" color="subtle" truncate w="full">
                            Send &ldquo;<span className="fs-mask">{debouncedInputValue}</span>&rdquo; to the AI Agent
                          </Text>
                        </Flex>
                      </Box>
                    </Command.Item>
                  ) : null}
                </Command.List>
              </Box>
              <div
                className={clsx(
                  styles.quickLookContainer,
                  (quickLook.isOpen === false || isQuickLookEnabled === false) && styles.quickLookContainerHidden,
                )}
              >
                {quickLook.isOpen && isQuickLookEnabled ? <QuickLookPanel result={selectedResult} /> : null}
              </div>
            </Flex>
          </Command>
          <InlineStack
            w="full"
            alignInline="space-between"
            py="100"
            px="200"
            alignBlock="center"
            className={styles.footer}
          >
            <Flex gap="050" alignItems="center">
              <KeyboardShortcut hotkey="Escape" />
              <Text size="xxs" color="subtle">
                Close
              </Text>
            </Flex>

            <InlineStack alignInline="space-between" gap="300">
              <Flex gap="050" alignItems="center">
                <KeyboardShortcut hotkey="ArrowUp" />
                <KeyboardShortcut hotkey="ArrowDown" />
                <Text size="xxs" color="subtle">
                  Navigate
                </Text>
              </Flex>
              <Flex gap="050" alignItems="center">
                <KeyboardShortcut hotkey="Enter" />
                <Text size="xxs" color="subtle">
                  Select
                </Text>
              </Flex>
              <Flex gap="050" alignItems="center">
                <KeyboardShortcut hotkey="Mod+Enter" />
                <Text size="xxs" color="subtle">
                  Open in new tab
                </Text>
              </Flex>
              {isQuickLookEnabled && quickLook.isOpen ? (
                <Flex gap="050" alignItems="center">
                  <KeyboardShortcut hotkey="ArrowLeft" />
                  <Text size="xxs" color="subtle">
                    Close
                  </Text>
                </Flex>
              ) : null}
              {isQuickLookEnabled && !quickLook.isOpen && quickLook.peekable ? (
                <Flex gap="050" alignItems="center">
                  <KeyboardShortcut hotkey="ArrowRight" />
                  <Text size="xxs" color="subtle">
                    Peek
                  </Text>
                </Flex>
              ) : null}
            </InlineStack>
          </InlineStack>
        </ModalPrimitiveDialog>
      </ModalPrimitiveOverlay>
    </ModalPrimitive>
  );
}
